/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module NetworkSecurityPolicyModule
 */

import { L10nService } from '@vmw/ngx-vip';
import { withFullModalMixin } from 'ajs/js/utilities/mixins';
import { RepeatedMessageItem } from
    'ajs/modules/data-model/factories/repeated-message-item.factory';
import {
    withEditChildMessageItemMixin,
} from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';
import { MessageItem } from
    'ajs/modules/data-model/factories/message-item.factory';
import { ObjectTypeItem } from
    'ajs/modules/data-model/factories/object-type-item.factory';

import { NetworkSecurityPolicy } from 'object-types';

import {
    AviPermissionResource,
    IConfigPbAttributes,
    INetworkSecurityPolicy,
    IRoleFilterMatchLabel,
} from 'generated-types';

import { NetworkSecurityPolicyModalComponent } from 'ng/modules/network-security-policy';
import { NetworkSecurityRuleConfigItem } from '.';
import * as l10n from '../network-security-policy.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type INetworkSecurityPolicyPartial = Omit<INetworkSecurityPolicy,
'rules' | 'markers' | 'configpb_attributes'>;

interface IExtendedNetworkSecurityPolicy extends INetworkSecurityPolicyPartial {
    rules?: RepeatedMessageItem<NetworkSecurityRuleConfigItem>;
    markers?: RepeatedMessageItem<MessageItem<IRoleFilterMatchLabel>>;
    configpb_attributes: IConfigPbAttributes
}

/**
 * Used for defining type of data.
 */
interface INetworkSecurityPolicyData {
    config: IExtendedNetworkSecurityPolicy;
}

/**
 * @description
 *
 *   Network Security Policy item.
 *
 * @author Sarthak Kapoor, Abhinesh Gour
 */
export class NetworkSecurityPolicyItem extends
    withEditChildMessageItemMixin(withFullModalMixin(ObjectTypeItem)) {
    public static ajsDependencies = [
        'policyRuleSort',
        'l10nService',
    ];

    public data: INetworkSecurityPolicyData;
    /** Overriding getConfig()'s return type */
    public getConfig: () => IExtendedNetworkSecurityPolicy;

    /**
     * Getter for rules.
     */
    public get rules(): RepeatedMessageItem<NetworkSecurityRuleConfigItem> {
        return this.getConfig().rules;
    }

    private readonly l10nService: L10nService;

    constructor(args: Record<string, any> = {}) {
        const extendedArgs = {
            objectName: 'networksecuritypolicy',
            windowElement: NetworkSecurityPolicyModalComponent,
            objectType: NetworkSecurityPolicy,
            permissionName: AviPermissionResource.PERMISSION_NETWORKSECURITYPOLICY,
            ...args,
        };

        super(extendedArgs);

        this.l10nService = this.getAjsDependency_('l10nService');

        this.l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Method to add NetworkSecurityRule.
     */
    public addNetworkSecurityRule(): void {
        this.addChildMessageItem({
            field: 'rules',
            modalBindings: {
                isEditing: false,
            },
        });
    }

    /**
     * Triggers edit for NetworkSecurityRule.
     */
    public editNetworkSecurityRule(rule: NetworkSecurityRuleConfigItem): void {
        this.editChildMessageItem({
            field: 'rules',
            messageItem: rule,
            modalBindings: {
                isEditing: true,
            },
        });
    }

    /**
     * @override
     */
    protected getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.networkSecurityPolicyModalBreadcrumbTitle);
    }
}
